import { useEffect, useRef, useCallback } from "react";
import { API } from "@/lib/api";

/**
 * WebSocket connection to /api/ws.
 * - Auto-reconnects with backoff (max 30 s).
 * - Sends a heartbeat ping every 25 s.
 * - Invokes `onMessage(data)` for each JSON payload received.
 * - Returns nothing; connection is managed for the lifetime of the component.
 */
export function useAppWebSocket(onMessage, enabled = true) {
  const wsRef = useRef(null);
  const reconnectRef = useRef(null);
  const pingRef = useRef(null);
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;

  const cleanup = useCallback(() => {
    if (pingRef.current) clearInterval(pingRef.current);
    if (reconnectRef.current) clearTimeout(reconnectRef.current);
    if (wsRef.current) {
      try { wsRef.current.onclose = null; wsRef.current.close(); } catch (e) {}
      wsRef.current = null;
    }
  }, []);

  const connect = useCallback((delay = 0) => {
    if (!enabled) return;
    reconnectRef.current = setTimeout(() => {
      const wsUrl = API.replace(/^http/i, "ws").replace(/\/api$/, "") + "/api/ws";
      let ws;
      try { ws = new WebSocket(wsUrl); } catch { return; }
      wsRef.current = ws;
      ws.onopen = () => {
        if (pingRef.current) clearInterval(pingRef.current);
        pingRef.current = setInterval(() => {
          try { ws.send("ping"); } catch (e) {}
        }, 25000);
      };
      ws.onmessage = (evt) => {
        try {
          const data = JSON.parse(evt.data);
          onMessageRef.current?.(data);
        } catch (e) { /* ignore */ }
      };
      ws.onclose = () => {
        if (pingRef.current) clearInterval(pingRef.current);
        // Backoff: start at 2 s, cap at 30 s
        const next = Math.min((delay || 2000) * 1.6, 30000);
        connect(next);
      };
      ws.onerror = () => { try { ws.close(); } catch (e) {} };
    }, delay);
  }, [enabled]);

  useEffect(() => {
    connect(0);
    return cleanup;
  }, [connect, cleanup]);
}
